import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { GraduationCap, Globe, Film, Calendar } from 'lucide-react';

const actions = [
  {
    icon: GraduationCap,
    title: 'Écoles & Universités',
    description: 'Diffusions gratuites pour les enfants et étudiants, pour sensibiliser les jeunes générations.',
  },
  {
    icon: Globe,
    title: 'Centres Culturels Français',
    description: "Diffusion à l'international via le réseau des Centres Culturels Français pour toucher de nouveaux publics.",
  },
  {
    icon: Film,
    title: 'Cinémaquis',
    description: 'Projections en Corse via notre partenaire Cinémaquis, pour des diffusions au plus près des territoires.',
  },
  {
    icon: Calendar,
    title: 'Festival WiPP',
    description: 'Organisation de deux journées de programmation lors de notre festival annuel, célébrant le cinéma engagé.',
  },
];

const CINEMA_IMAGE = "https://images.pexels.com/photos/7991233/pexels-photo-7991233.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const ActionsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="py-24 md:py-32 bg-[#0a0a0c]" data-testid="actions-section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-2xl overflow-hidden img-zoom aspect-[4/5]">
              <img
                src={CINEMA_IMAGE}
                alt="Audience dans une salle de cinéma"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-6 glass p-6 rounded-2xl max-w-xs">
              <p className="text-[#D4AF37] font-medium mb-1">Notre mission</p>
              <p className="text-sm text-[#a1a1aa]">
                {"Sans l'achat de droits de diffusion, aucune projection n'est possible."}
              </p>
            </div>
          </motion.div>

          {/* Right Content */}
          <div ref={ref} className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <span className="overline mb-4 block">Nos actions</span>
              <h2 className="text-3xl md:text-5xl font-medium mb-6">
                Donner accès au cinéma
                <br />
                <span className="text-gradient">à ceux qui en sont privés</span>
              </h2>
              <p className="text-[#a1a1aa] text-lg max-w-xl">
                {"L'ensemble des fonds collectés auprès de nos mécènes sert exclusivement à financer l'achat de droits de diffusion et l'organisation de ces actions."}
              </p>
            </motion.div>

            <div className="space-y-4">
              {actions.map((action, index) => (
                <motion.div
                  key={action.title}
                  initial={{ opacity: 0, x: 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors"
                  data-testid={`action-item-${index}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37]">
                    <action.icon size={22} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">{action.title}</h3>
                    <p className="text-[#a1a1aa] text-sm leading-relaxed">
                      {action.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
